import { useState } from 'react';
import { Button, TextField, type SelectFieldOption } from '@humanforce/mango-ui-react';
import { DetailPair, SectionShell } from '../components/SectionShell';
import { MangoSelectField } from '../components/MangoSelectField';
import { RetroactiveReasonField } from '../components/RetroactiveReasonField';
import { WorkerDrawer } from '../components/WorkerDrawer';
import { updateWorkerDetail, type ReferenceData } from '../workersApi';
import { GENDER_LABELS, type Gender, type PersonDetailResponse } from '../workersApiTypes';
import { hasErrors, isRetroactive, validatePersonDetail, type FieldErrors } from '../workersValidation';
import { trimOrUndefined } from '../workersUtils';

interface PersonDetailsSectionProps {
  person: PersonDetailResponse;
  reference: ReferenceData | null;
  readOnly: boolean;
  onUpdated: () => void;
}

interface DraftState {
  title: string;
  legalFirstName: string;
  legalMiddleName: string;
  legalLastName: string;
  preferredFirstName: string;
  pronouns: string;
  dateOfBirth: string;
  gender: string;
  effectiveFrom: string;
  retroactiveReason: string;
}

const toDraft = (p: PersonDetailResponse): DraftState => ({
  title: p.title ?? '', legalFirstName: p.legalFirstName ?? '', legalMiddleName: p.legalMiddleName ?? '',
  legalLastName: p.legalLastName ?? '', preferredFirstName: p.preferredFirstName ?? '', pronouns: p.pronouns ?? '',
  dateOfBirth: p.dateOfBirth?.slice(0, 10) ?? '', gender: p.gender ?? '', effectiveFrom: '', retroactiveReason: '',
});

export const PersonDetailsSection = ({ person, reference, readOnly, onUpdated }: PersonDetailsSectionProps) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DraftState>(() => toDraft(person));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();

  const openDrawer = () => { setDraft(toDraft(person)); setErrors({}); setSaveError(undefined); setOpen(true); };
  const set = (key: keyof DraftState) => (value: string) => setDraft((d) => ({ ...d, [key]: value }));

  const titleOptions: SelectFieldOption[] = [{ label: '— None —', value: '' }, ...(reference?.titles ?? []).map((t) => ({ label: t, value: t }))];
  const genderOptions: SelectFieldOption[] = (reference?.genders ?? []).map((g) => ({ label: GENDER_LABELS[g as Gender] ?? g, value: g }));
  const showRetro = isRetroactive(draft.effectiveFrom || undefined);

  const save = async () => {
    setSaveError(undefined);
    const validation = validatePersonDetail({
      legalFirstName: draft.legalFirstName, legalLastName: draft.legalLastName, dateOfBirth: draft.dateOfBirth,
      gender: draft.gender, title: draft.title || undefined, effectiveFrom: draft.effectiveFrom || undefined,
      retroactiveReason: draft.retroactiveReason || undefined,
    });
    setErrors(validation);
    if (hasErrors(validation)) return;

    setSaving(true);
    const result = await updateWorkerDetail(person.personId ?? '', {
      title: trimOrUndefined(draft.title),
      legalFirstName: draft.legalFirstName.trim(),
      legalMiddleName: trimOrUndefined(draft.legalMiddleName),
      legalLastName: draft.legalLastName.trim(),
      preferredFirstName: trimOrUndefined(draft.preferredFirstName),
      pronouns: trimOrUndefined(draft.pronouns),
      dateOfBirth: draft.dateOfBirth,
      gender: draft.gender,
      // Carry forward HR-mastered custom fields: the backend builds a new effective-dated
      // PersonDetails version straight from this request, so omitting intellihrCustomFields would
      // silently blank it on the new version when editing visible fields like name/DOB.
      intellihrCustomFields: person.intellihrCustomFields ?? undefined,
      effectiveFrom: trimOrUndefined(draft.effectiveFrom),
      retroactiveReason: trimOrUndefined(draft.retroactiveReason),
    });
    setSaving(false);
    if (result.ok) { setOpen(false); onUpdated(); }
    else setSaveError(result.message ?? 'Could not save the person details.');
  };

  return (
    <SectionShell
      title="Person details"
      description="Legal identity for this person."
      action={!readOnly && <Button variant="secondary" label="Edit" icon="edit" onClick={openDrawer} />}
    >
      <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-l">
        <DetailPair label="Title" value={person.title} />
        <DetailPair label="Preferred name" value={person.preferredFirstName} />
        <DetailPair label="Legal first name" value={person.legalFirstName} />
        <DetailPair label="Legal middle name" value={person.legalMiddleName} />
        <DetailPair label="Legal last name" value={person.legalLastName} />
        <DetailPair label="Pronouns" value={person.pronouns} />
        <DetailPair label="Date of birth" value={person.dateOfBirth?.slice(0, 10)} />
        <DetailPair label="Gender" value={person.gender ? GENDER_LABELS[person.gender as Gender] ?? person.gender : undefined} />
      </div>

      <WorkerDrawer
        open={open}
        title="Edit person details"
        subtitle="Changes create a new effective-dated version."
        onClose={() => setOpen(false)}
        onSave={save}
        saving={saving}
        saveLabel="Save"
        error={saveError}
      >
        <div className="mgo-stack mgo-gap-l">
          <MangoSelectField label="Title" value={draft.title} options={titleOptions} onChange={set('title')} error={errors.title} />
          <TextField label="Legal first name" required value={draft.legalFirstName} onChange={(e) => set('legalFirstName')(e.target.value)} status={errors.legalFirstName ? 'critical' : undefined} statusText={errors.legalFirstName} />
          <TextField label="Legal middle name" value={draft.legalMiddleName} onChange={(e) => set('legalMiddleName')(e.target.value)} />
          <TextField label="Legal last name" required value={draft.legalLastName} onChange={(e) => set('legalLastName')(e.target.value)} status={errors.legalLastName ? 'critical' : undefined} statusText={errors.legalLastName} />
          <TextField label="Preferred first name" value={draft.preferredFirstName} onChange={(e) => set('preferredFirstName')(e.target.value)} />
          <TextField label="Pronouns" value={draft.pronouns} onChange={(e) => set('pronouns')(e.target.value)} />
          <TextField label="Date of birth" type="date" required value={draft.dateOfBirth} onChange={(e) => set('dateOfBirth')(e.target.value)} status={errors.dateOfBirth ? 'critical' : undefined} statusText={errors.dateOfBirth} />
          <MangoSelectField label="Gender" required value={draft.gender} options={genderOptions} onChange={set('gender')} error={errors.gender} />
          <TextField label="Effective from" type="date" value={draft.effectiveFrom} onChange={(e) => set('effectiveFrom')(e.target.value)} helpText="Leave blank to take effect now." />
          <RetroactiveReasonField show={showRetro} value={draft.retroactiveReason} onChange={set('retroactiveReason')} error={errors.retroactiveReason} />
        </div>
      </WorkerDrawer>
    </SectionShell>
  );
};
